import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatZAR, formatInt, formatApiError } from "@/lib/api";
import { PageHeader, KpiCard } from "@/components/Kpi";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line, Legend, ComposedChart, Area } from "recharts";
import { Link } from "react-router-dom";
import { Megaphone, TrendingUp, Clock, Calendar, Upload, Trash2, ArrowUpRight, AlertTriangle, TrendingDown, Sparkles, Package, Edit3, ChevronRight, Info } from "lucide-react";

const PERIODS = [
    { label: "30D", days: 30 },
    { label: "90D", days: 90 },
    { label: "365D", days: 365 },
    { label: "ALL", days: 3650 },
];

const TABS = [
    { key: "campaigns", label: "Ad Campaigns" },
    { key: "timing", label: "Timing insights" },
];

export default function Marketing() {
    const [tab, setTab] = useState("campaigns");

    return (
        <div>
            <PageHeader title="Marketing & Ads insights" subtitle="Track your Takealot campaigns and find the best windows to spend">
                <div className="inline-flex border border-[color:var(--bl-border)] rounded-sm bg-[color:var(--bl-surface)]" data-testid="mkt-tabs">
                    {TABS.map((t) => (
                        <button key={t.key} data-testid={`mkt-tab-${t.key}`} onClick={() => setTab(t.key)}
                            className={`px-3 py-1.5 text-xs uppercase tracking-wider transition ${tab === t.key ? "bg-[color:var(--bl-takealot-blue)] text-white" : "text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)]"}`}>
                            {t.label}
                        </button>
                    ))}
                </div>
            </PageHeader>
            {tab === "campaigns" ? <AdCampaigns /> : <TimingInsights />}
        </div>
    );
}

// ============================================================================
// AD CAMPAIGNS — Takealot XLSX export ingestion + ROAS analysis
// ============================================================================

const SEVERITY_STYLES = {
    red: { border: "border-[color:var(--bl-danger)]", bg: "bg-[color:var(--bl-danger)]/8", text: "text-[color:var(--bl-danger)]", icon: TrendingDown },
    amber: { border: "border-[color:var(--bl-warning)]", bg: "bg-[color:var(--bl-warning)]/8", text: "text-[color:var(--bl-warning)]", icon: Sparkles },
    green: { border: "border-[color:var(--bl-success)]", bg: "bg-[color:var(--bl-success)]/8", text: "text-[color:var(--bl-success)]", icon: TrendingUp },
};

function AdCampaigns() {
    const [days, setDays] = useState(30);
    const [summary, setSummary] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [products, setProducts] = useState([]);
    const [editing, setEditing] = useState(null); // campaign_id being edited
    const [editForm, setEditForm] = useState({ name: "", product_sku: "" });
    const fileRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const [s, c, i, p] = await Promise.all([
                api.get(`/marketing/ads/summary?days=${days}`),
                api.get(`/marketing/ads/campaigns?days=${days}`),
                api.get(`/marketing/ads/insights?days=${days}`),
                api.get("/products"),
            ]);
            setSummary(s.data);
            setCampaigns(c.data.campaigns || []);
            setInsights(i.data.insights || []);
            setProducts(p.data.items || p.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-line */ }, [days]);

    const onDrop = async (e) => {
        e.preventDefault();
        const files = [...(e.dataTransfer?.files || [])];
        if (files.length) await uploadFiles(files);
    };
    const onPick = async (e) => {
        const files = [...(e.target.files || [])];
        if (files.length) await uploadFiles(files);
        if (fileRef.current) fileRef.current.value = "";
    };
    const uploadFiles = async (files) => {
        setUploading(true);
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            const r = await api.post("/marketing/ads/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const ok = r.data.results.filter((x) => x.ok);
            const bad = r.data.results.filter((x) => !x.ok);
            if (ok.length) toast.success(`Uploaded ${ok.length} file${ok.length > 1 ? "s" : ""} · ${ok.reduce((s, x) => s + x.rows, 0)} rows`);
            bad.forEach((b) => toast.error(`${b.filename}: ${b.error}`));
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setUploading(false); }
    };
    const deleteCampaign = async (c) => {
        if (!window.confirm(`Delete "${c.name}" and all its ${c.metrics?.days || 0} days of data? This cannot be undone.`)) return;
        try {
            await api.delete(`/marketing/ads/campaigns/${c.campaign_id}`);
            toast.success(`Deleted ${c.name}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const startEdit = (c) => { setEditing(c.campaign_id); setEditForm({ name: c.name || "", product_sku: c.product_sku || "" }); };
    const saveEdit = async () => {
        try {
            await api.patch(`/marketing/ads/campaigns/${editing}`, editForm);
            toast.success("Campaign updated");
            setEditing(null);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading && !summary) return <div className="text-[color:var(--bl-text-dim)]">Loading…</div>;

    const hasData = summary && summary.spend > 0;

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div className="inline-flex border border-[color:var(--bl-border)] rounded-sm bg-[color:var(--bl-surface)]" data-testid="ads-period-tabs">
                    {PERIODS.map((p) => (
                        <button key={p.days} data-testid={`ads-period-${p.label}`} onClick={() => setDays(p.days)}
                            className={`px-3 py-1.5 text-xs uppercase tracking-wider ${days === p.days ? "bg-[color:var(--bl-takealot-blue)] text-white" : "text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)]"}`}>
                            {p.label}
                        </button>
                    ))}
                </div>
                <div className="text-[10px] text-[color:var(--bl-text-dim)] flex items-center gap-1">
                    <Info className="w-3 h-3" /> Break-even uses avg Takealot fee of {summary?.avg_fee_pct ?? 25}% + your COGS
                </div>
            </div>

            {/* Upload dropzone */}
            <div
                data-testid="ads-dropzone"
                onDrop={onDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-[color:var(--bl-border)] hover:border-[color:var(--bl-takealot-blue)] transition rounded-sm p-6 bg-[color:var(--bl-surface)] text-center cursor-pointer mb-6"
            >
                <input ref={fileRef} type="file" multiple accept=".xlsx" onChange={onPick} className="hidden" data-testid="ads-file-input" />
                <Upload className="w-6 h-6 mx-auto mb-2 text-[color:var(--bl-text-dim)]" />
                <div className="text-sm">
                    {uploading ? "Uploading…" : "Drop Takealot Ads XLSX exports here"}
                    <span className="text-[color:var(--bl-text-dim)]"> or click to browse</span>
                </div>
                <div className="text-[10px] text-[color:var(--bl-text-dim)] mt-1 font-mono-num">
                    Campaigns_Product_Ads_Performance_Summary__R(YYYYMMDD-YYYYMMDD)__E(...)__ID(...).xlsx · Multiple files supported
                </div>
            </div>

            {!hasData ? (
                <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-8 rounded-sm text-center text-[color:var(--bl-text-dim)]" data-testid="ads-empty">
                    <Megaphone className="w-8 h-8 mx-auto mb-3 opacity-40" />
                    <div className="text-sm mb-1">No campaign data yet in the last {days} days</div>
                    <div className="text-[11px]">Export a campaign summary from Takealot Seller Portal → Ads → click any campaign → Download → drop the XLSX above.</div>
                </div>
            ) : (
                <>
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="ads-kpi-row">
                        <KpiCard label="Ad Spend" value={formatZAR(summary.spend)} sub={`${formatInt(summary.impressions)} impressions`} testid="ads-kpi-spend" accent="orange" />
                        <KpiCard label="Ad Revenue" value={formatZAR(summary.revenue)} sub={`${summary.orders} orders`} testid="ads-kpi-revenue" />
                        <KpiCard label="Blended ROAS" value={`${summary.roas.toFixed(2)}×`} sub={`CPC ${formatZAR(summary.cpc)} · CTR ${summary.ctr}%`} testid="ads-kpi-roas" accent="coffee" />
                        <KpiCard label="Halo revenue" value={`${summary.halo_pct}%`} sub={`${formatZAR(summary.other_sku_revenue)} from other SKUs`} testid="ads-kpi-halo" />
                    </div>

                    {/* Insights */}
                    {insights.length > 0 && (
                        <div className="mb-6" data-testid="ads-insights">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] mb-2">Recommendations</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {insights.map((ins, idx) => {
                                    const s = SEVERITY_STYLES[ins.severity] || SEVERITY_STYLES.amber;
                                    const Icon = s.icon;
                                    return (
                                        <div key={idx} className={`border-l-4 ${s.border} ${s.bg} p-3 rounded-sm shadow-sm`} data-testid={`insight-${ins.severity}-${idx}`}>
                                            <div className={`flex items-center gap-2 text-xs font-semibold ${s.text} uppercase tracking-wider mb-1`}>
                                                <Icon className="w-4 h-4" /> {ins.title}
                                            </div>
                                            <div className="text-sm text-[color:var(--bl-text)]"><b>{ins.name}</b>: {ins.detail}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Daily Spend vs Revenue Chart */}
                    <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm mb-6" data-testid="ads-daily-chart">
                        <div className="flex items-center gap-2 mb-3 text-[color:var(--bl-takealot-blue)]">
                            <TrendingUp className="w-4 h-4" />
                            <div className="font-serif-display text-xl text-[color:var(--bl-text)]">Daily spend vs ad revenue</div>
                        </div>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={summary.daily}>
                                    <CartesianGrid stroke="#d8c8ad" strokeDasharray="3 6" vertical={false} />
                                    <XAxis dataKey="date" stroke="#75614f" fontSize={10} />
                                    <YAxis yAxisId="l" stroke="#75614f" fontSize={10} tickFormatter={(v) => `R${Math.round(v)}`} />
                                    <YAxis yAxisId="r" orientation="right" stroke="#6b4423" fontSize={10} />
                                    <Tooltip contentStyle={{ background: "#fffaf2", border: "1px solid #d8c8ad", fontSize: 12, color: "#2b1d15" }} formatter={(v, n) => n === "ROAS" ? `${v}×` : formatZAR(v)} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Bar yAxisId="l" dataKey="spend" name="Spend" fill="#e35a00" radius={[2, 2, 0, 0]} />
                                    <Bar yAxisId="l" dataKey="revenue" name="Ad Revenue" fill="#0079c2" radius={[2, 2, 0, 0]} />
                                    <Line yAxisId="r" dataKey="roas" name="ROAS" stroke="#6b4423" strokeWidth={2} dot={{ r: 3 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Campaigns Table */}
                    <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm shadow-sm overflow-hidden mb-6" data-testid="ads-campaigns-table">
                        <div className="p-4 border-b border-[color:var(--bl-border)] flex items-center gap-2">
                            <Megaphone className="w-4 h-4 text-[color:var(--bl-takealot-orange)]" />
                            <div className="font-serif-display text-lg">Campaigns</div>
                            <div className="text-xs text-[color:var(--bl-text-dim)]">· {campaigns.length} total</div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)] bg-[color:var(--bl-bg)]">
                                    <tr>
                                        <th className="text-left py-2 px-3">Campaign</th>
                                        <th className="text-right py-2 px-3">Spend</th>
                                        <th className="text-right py-2 px-3">Revenue</th>
                                        <th className="text-right py-2 px-3">ROAS</th>
                                        <th className="text-right py-2 px-3">Break-even</th>
                                        <th className="text-right py-2 px-3">Orders</th>
                                        <th className="text-right py-2 px-3">Budget</th>
                                        <th className="text-right py-2 px-3">Halo</th>
                                        <th className="text-right py-2 px-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campaigns.map((c) => {
                                        const m = c.metrics;
                                        const be = c.break_even_roas;
                                        const roasClass = be && m.roas > 0 ? (m.roas >= be * 1.2 ? "text-[color:var(--bl-success)]" : m.roas >= be ? "text-[color:var(--bl-text)]" : "text-[color:var(--bl-danger)]") : (m.roas >= 1.5 ? "text-[color:var(--bl-success)]" : m.roas >= 1 ? "text-[color:var(--bl-text)]" : "text-[color:var(--bl-danger)]");
                                        const budgetClass = m.avg_budget_pct != null && m.avg_budget_pct < 100 ? "text-[color:var(--bl-warning)]" : "text-[color:var(--bl-text-dim)]";
                                        const haloPct = m.revenue > 0 ? Math.round(m.other_sku_revenue / m.revenue * 100) : 0;
                                        return (
                                            <tr key={c.campaign_id} data-testid={`campaign-row-${c.campaign_id}`} className="border-b border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]/50">
                                                <td className="py-2 px-3">
                                                    <div className="flex flex-col">
                                                        <span className="text-[color:var(--bl-text)] font-medium">{c.name}</span>
                                                        {c.product ? (
                                                            <span className="text-[10px] text-[color:var(--bl-text-dim)] font-mono-num flex items-center gap-1">
                                                                <Package className="w-2.5 h-2.5" /> {c.product.sku} · {c.product.title}
                                                            </span>
                                                        ) : (
                                                            <span className="text-[10px] text-[color:var(--bl-warning)]">Link a SKU to unlock profit tracking →</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-2 px-3 text-right font-mono-num">{formatZAR(m.spend)}</td>
                                                <td className="py-2 px-3 text-right font-mono-num">{formatZAR(m.revenue)}</td>
                                                <td className={`py-2 px-3 text-right font-mono-num font-semibold ${roasClass}`}>{m.roas.toFixed(2)}×</td>
                                                <td className="py-2 px-3 text-right font-mono-num text-[color:var(--bl-text-dim)]">{be ? `${be.toFixed(2)}×` : "—"}</td>
                                                <td className="py-2 px-3 text-right font-mono-num">{m.orders}</td>
                                                <td className={`py-2 px-3 text-right font-mono-num ${budgetClass}`}>
                                                    {m.avg_budget_pct != null ? `${m.avg_budget_pct}%` : "—"}
                                                    {m.days_capped > 0 && <div className="text-[9px]">cap {m.days_capped}/{m.days}d</div>}
                                                </td>
                                                <td className="py-2 px-3 text-right font-mono-num text-[color:var(--bl-text-dim)]">{haloPct}%</td>
                                                <td className="py-2 px-3 text-right whitespace-nowrap">
                                                    <button data-testid={`campaign-edit-${c.campaign_id}`} onClick={() => startEdit(c)} className="text-[color:var(--bl-takealot-blue)] hover:brightness-125 p-1" title="Edit name / link SKU">
                                                        <Edit3 className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button data-testid={`campaign-delete-${c.campaign_id}`} onClick={() => deleteCampaign(c)} className="text-[color:var(--bl-danger)] hover:brightness-125 p-1 ml-1" title="Delete">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* Edit modal */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="edit-modal" onClick={() => setEditing(null)}>
                    <div className="bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] rounded-sm p-5 max-w-md w-full shadow-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="font-serif-display text-xl mb-3">Edit campaign</div>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] mb-1">Name</label>
                        <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm mb-3 focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                            data-testid="edit-name" />
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] mb-1">Link to SKU (unlocks profit calc)</label>
                        <select value={editForm.product_sku || ""} onChange={(e) => setEditForm({ ...editForm, product_sku: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm mb-4 focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                            data-testid="edit-sku">
                            <option value="">— not linked —</option>
                            {products.map((p) => (
                                <option key={p.sku} value={p.sku}>{p.sku} · {p.title || p.name_override || ""}</option>
                            ))}
                        </select>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setEditing(null)} className="border border-[color:var(--bl-border)] px-3 py-1.5 text-sm rounded-sm hover:bg-[color:var(--bl-surface-2)]">Cancel</button>
                            <button onClick={saveEdit} data-testid="edit-save" className="bg-[color:var(--bl-takealot-blue)] text-white px-3 py-1.5 text-sm rounded-sm hover:brightness-110">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// TIMING INSIGHTS — existing "when to advertise" heatmaps (unchanged behavior)
// ============================================================================

function HourHeatmap({ byHour }) {
    const max = Math.max(1, ...byHour.map((h) => h.units));
    return (
        <div data-testid="hour-heatmap">
            <div className="text-xs text-[color:var(--bl-text-dim)] mb-2">Best ad-boost windows by hour of day</div>
            <div className="grid grid-cols-24 gap-[2px]" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
                {byHour.map((h) => {
                    const intensity = h.units / max;
                    const bg = `rgba(0, 121, 194, ${0.08 + intensity * 0.85})`;
                    return (
                        <div key={h.hour}
                            title={`${h.hour}:00 — ${h.units} units · ${formatZAR(h.revenue)}`}
                            className="aspect-square flex items-center justify-center text-[9px] font-mono-num rounded-sm border border-[color:var(--bl-border)]"
                            style={{ background: bg, color: intensity > 0.5 ? "#fff" : "#75614f" }}>
                            {h.hour}
                        </div>
                    );
                })}
            </div>
            <div className="text-[10px] text-[color:var(--bl-text-dim)] mt-1 flex justify-between">
                <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
            </div>
        </div>
    );
}

function TimingInsights() {
    const [days, setDays] = useState(90);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/insights/marketing?days=${days}`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [days]);

    if (loading && !data) return <div className="text-[color:var(--bl-text-dim)]">Loading…</div>;
    if (!data) return null;

    const chartTip = { background: "#fffaf2", border: "1px solid #d8c8ad", fontSize: 12, color: "#2b1d15" };
    const top = data.low_conversion.filter((l) => l.page_views_30d > 0).slice(0, 8);
    const topDow = data.top_dow || [];
    const topHours = data.top_hours || [];
    const topDowLabel = topDow.map((d) => d.name).join(" · ") || "—";
    const topHourLabel = topHours.map((h) => `${h.hour}:00`).join(" · ") || "—";

    return (
        <div>
            <div className="inline-flex border border-[color:var(--bl-border)] rounded-sm bg-[color:var(--bl-surface)] mb-4">
                {PERIODS.map((p) => (
                    <button key={p.days} data-testid={`mkt-period-${p.label}`} onClick={() => setDays(p.days)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-wider ${days === p.days ? "bg-[color:var(--bl-takealot-blue)] text-white" : "text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)]"}`}>
                        {p.label}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                <KpiCard label="Period revenue" value={formatZAR(data.period_revenue)} sub={`${data.period_units} units`} testid="mkt-kpi-revenue" />
                <KpiCard label="Top days" value={topDowLabel} accent="orange" sub="boost ads on these" testid="mkt-kpi-top-dow" />
                <KpiCard label="Top hours" value={topHourLabel} accent="coffee" sub="dayparting opportunity" testid="mkt-kpi-top-hours" />
            </div>

            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm shadow-sm p-5 mb-6" data-testid="low-conversion-card">
                <div className="flex items-center gap-2 mb-2 text-[color:var(--bl-takealot-orange)]">
                    <Megaphone className="w-4 h-4" />
                    <div className="font-serif-display text-xl text-[color:var(--bl-text)]">Boost candidates — high views, low conversion</div>
                </div>
                <p className="text-xs text-[color:var(--bl-text-dim)] mb-3">
                    Opportunity score = lost views × gap to a 20% conversion benchmark. Higher = bigger ad-boost upside.
                </p>
                {top.length === 0 ? (
                    <div className="text-sm text-[color:var(--bl-text-dim)] py-6 text-center">Sync some data first — no views to analyse.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="low-conversion-table">
                        <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)]">
                            <tr>
                                <th className="text-left py-2">Product</th>
                                <th className="text-right py-2">Views 30d</th>
                                <th className="text-right py-2">Units 30d</th>
                                <th className="text-right py-2">Conv %</th>
                                <th className="text-right py-2">Lost views</th>
                                <th className="text-right py-2">Opportunity</th>
                                <th className="text-right py-2">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {top.map((r) => (
                                <tr key={r.sku} data-testid={`low-conv-row-${r.sku}`} className="border-b border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]/50">
                                    <td className="py-2">
                                        <Link to={`/products/${encodeURIComponent(r.sku)}`} className="flex items-center gap-2 hover:opacity-80">
                                            {r.image_url && <img src={r.image_url} alt="" className="w-8 h-8 object-cover rounded-sm border border-[color:var(--bl-border)] bg-white" />}
                                            <div>
                                                <div className="line-clamp-1 text-[color:var(--bl-text)]">{r.title}</div>
                                                <div className="font-mono-num text-[10px] text-[color:var(--bl-text-dim)]">{r.sku} · LQ {r.listing_quality ?? "—"}</div>
                                            </div>
                                        </Link>
                                    </td>
                                    <td className="py-2 text-right font-mono-num">{formatInt(r.page_views_30d)}</td>
                                    <td className="py-2 text-right font-mono-num">{formatInt(r.units_30d)}</td>
                                    <td className={`py-2 text-right font-mono-num ${r.conversion_pct < 10 ? "text-[color:var(--bl-danger)]" : r.conversion_pct < 20 ? "text-[color:var(--bl-warning)]" : "text-[color:var(--bl-success)]"}`}>
                                        {r.conversion_pct.toFixed(1)}%
                                    </td>
                                    <td className="py-2 text-right font-mono-num text-[color:var(--bl-text-dim)]">{formatInt(r.lost_views)}</td>
                                    <td className="py-2 text-right font-mono-num font-semibold text-[color:var(--bl-takealot-orange)]">{formatInt(r.opportunity_score)}</td>
                                    <td className="py-2 text-right">
                                        <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-[color:var(--bl-takealot-orange)]/15 text-[color:var(--bl-takealot-orange)] rounded-sm">
                                            {r.conversion_pct < 10 ? "Improve listing" : r.conversion_pct < 20 ? "Boost ads" : "Optimise"}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm" data-testid="dow-card">
                    <div className="flex items-center gap-2 mb-3 text-[color:var(--bl-takealot-blue)]">
                        <TrendingUp className="w-4 h-4" />
                        <div className="font-serif-display text-xl text-[color:var(--bl-text)]">By day of week</div>
                    </div>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.by_dow}>
                                <CartesianGrid stroke="#d8c8ad" strokeDasharray="3 6" vertical={false} />
                                <XAxis dataKey="name" stroke="#75614f" fontSize={11} />
                                <YAxis yAxisId="l" stroke="#75614f" fontSize={10} tickFormatter={(v) => `R${Math.round(v / 1000)}k`} />
                                <YAxis yAxisId="r" orientation="right" stroke="#6b4423" fontSize={10} />
                                <Tooltip contentStyle={chartTip} formatter={(v, n) => n === "Revenue" ? formatZAR(v) : v} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar yAxisId="l" dataKey="revenue" name="Revenue" fill="#0079c2" radius={[2, 2, 0, 0]} />
                                <Bar yAxisId="r" dataKey="units" name="Units" fill="#e35a00" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm" data-testid="hour-card">
                    <div className="flex items-center gap-2 mb-3 text-[color:var(--bl-takealot-blue)]">
                        <Clock className="w-4 h-4" />
                        <div className="font-serif-display text-xl text-[color:var(--bl-text)]">By hour of day</div>
                    </div>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.by_hour}>
                                <CartesianGrid stroke="#d8c8ad" strokeDasharray="3 6" vertical={false} />
                                <XAxis dataKey="hour" stroke="#75614f" fontSize={10} />
                                <YAxis stroke="#75614f" fontSize={10} />
                                <Tooltip contentStyle={chartTip} formatter={(v, n) => n === "Revenue" ? formatZAR(v) : v} labelFormatter={(h) => `${h}:00 - ${h}:59`} />
                                <Bar dataKey="units" name="Units" fill="#6b4423" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-4">
                        <HourHeatmap byHour={data.by_hour} />
                    </div>
                </div>
            </div>

            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm mb-6" data-testid="month-card">
                <div className="flex items-center gap-2 mb-3 text-[color:var(--bl-takealot-blue)]">
                    <Calendar className="w-4 h-4" />
                    <div className="font-serif-display text-xl text-[color:var(--bl-text)]">Monthly trend & seasonality</div>
                </div>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.by_month}>
                            <CartesianGrid stroke="#d8c8ad" strokeDasharray="3 6" vertical={false} />
                            <XAxis dataKey="month" stroke="#75614f" fontSize={10} />
                            <YAxis yAxisId="l" stroke="#75614f" fontSize={10} tickFormatter={(v) => `R${Math.round(v / 1000)}k`} />
                            <YAxis yAxisId="r" orientation="right" stroke="#6b4423" fontSize={10} />
                            <Tooltip contentStyle={chartTip} formatter={(v, n) => n === "Revenue" ? formatZAR(v) : v} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line yAxisId="l" dataKey="revenue" name="Revenue" stroke="#0079c2" strokeWidth={2} dot={{ r: 3 }} />
                            <Line yAxisId="r" dataKey="units" name="Units" stroke="#6b4423" strokeWidth={1.5} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-5 rounded-sm shadow-sm" data-testid="dom-card">
                <div className="font-serif-display text-xl mb-3">Pay-day spikes — by day of month</div>
                <p className="text-xs text-[color:var(--bl-text-dim)] mb-3">Look for spikes around the 25th-1st (salary cycles in SA) to time ad boosts.</p>
                <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.by_day_of_month}>
                            <CartesianGrid stroke="#d8c8ad" strokeDasharray="3 6" vertical={false} />
                            <XAxis dataKey="day" stroke="#75614f" fontSize={10} />
                            <YAxis stroke="#75614f" fontSize={10} />
                            <Tooltip contentStyle={chartTip} formatter={(v, n) => n === "Revenue" ? formatZAR(v) : v} />
                            <Bar dataKey="units" fill="#e35a00" radius={[2, 2, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
